import { Link } from '@chakra-ui/react';
import { FUN_TOKEN_DECIMALS, LAUNCH_TOKEN_FEE_AGENTFUN } from '@cronos-app/sdk';
import { InputCard } from '@ui/components/Input/InputCard';
import { InputChips } from '@ui/components/Input/InputChips';
import { TokenSelector } from '@ui/components/Input/TokenSelector';
import { MaskIcon } from '@ui/components/MaskIcon';
import { UsdValue } from '@ui/components/Num/UsdValue';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { Token } from '@ui/config/token';
import { useAgentFunBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenPrice } from '@ui/hooks/tokenPrice';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import {
  validateMaxValue,
  validateNotEmpty,
  validateNumberFormat,
} from '@ui/utils/validations';
import { Fraction } from 'bi-fraction';
import { useTranslations } from 'next-intl';
import { type FC, useState } from 'react';

import { useCreateAgentModal } from '../useCreateAgentModal';

export const CreateAgentAmountInput: FC = () => {
  const t = useTranslations();
  const chainLayer = useGlobalChainLayer();
  const setBuyAmount = useCreateAgentModal((s) => s.setBuyAmount);
  const buyAmount = useCreateAgentModal((s) => s.buyAmount);
  const agentFunBalance = useAgentFunBalance().data;
  const priceRet = useTokenPrice({ token: Token.agentFun });

  const [inputValue, setInputValue] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    const fractionValue = Fraction.tryParse(value) ?? Fraction.ZERO;
    if (agentFunBalance && fractionValue.lte(agentFunBalance)) {
      setBuyAmount(fractionValue);
    }
  };
  const maxValue = (agentFunBalance ?? Fraction.ZERO).sub(
    LAUNCH_TOKEN_FEE_AGENTFUN,
  );
  const error =
    validateNotEmpty({ t, value: inputValue }) ||
    validateNumberFormat({ t, value: inputValue }) ||
    validateMaxValue({
      t,
      value: inputValue,
      maxValue,
    });

  return (
    <InputCard
      value={inputValue}
      placeholder="Enter Buy Amount"
      onChange={handleInputChange}
      error={error}
      usd={<UsdValue value={buyAmount} price={priceRet.data?.usd_price} />}
      tokenSelector={
        <TokenSelector
          token={Token.agentFun}
          footer={
            <Link
              href={EXTERNAL_LINK.swapAgentFun(chainLayer)}
              target="_blank"
              color="currentcolor"
              gap="0"
            >
              Buy AGENTFUN
              <MaskIcon
                display="inline-block"
                icon="url(/masks/arrow-outward.svg)"
                boxSize="16px"
                bgColor="currentcolor"
              />
            </Link>
          }
        />
      }
      inputChips={
        <InputChips
          value={inputValue}
          maxValue={maxValue}
          decimalPlaces={FUN_TOKEN_DECIMALS}
          onChange={(value) => {
            const fractionValue = Fraction.tryParse(value) ?? Fraction.ZERO;
            setBuyAmount(fractionValue);
            setInputValue(value);
          }}
        />
      }
    />
  );
};
